import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Activity, Building2, CircleOff, Database, Edit3, Loader2, Plus, Power, RefreshCw, Server, ShieldCheck } from 'lucide-react';
import api from 'services/api';
import NotificationToast from 'components/common/NotificationToast';
import { usePermissions } from 'hooks/usePermissions';
import IxcSourceModal from './IxcSourceModal';
import ixcSourceService from '../services/ixcSourceService';

const formatDate = (value) => value
  ? new Intl.DateTimeFormat('pt-BR', { dateStyle: 'short', timeStyle: 'short' }).format(new Date(value))
  : 'Ainda não testada';

const IxcSources = () => {
  const { hasPermission } = usePermissions();
  const canView = hasPermission('config.ixc_sources.view');
  const canManage = hasPermission('config.ixc_sources.manage');
  const [sources, setSources] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [modal, setModal] = useState({ open: false, source: null });
  const [notification, setNotification] = useState(null);

  const notify = useCallback((message, type = 'success') => {
    setNotification({ message, type });
  }, []);

  const load = useCallback(async () => {
    if (!canView) return;
    setLoading(true);
    try {
      const [sourceResponse, companyResponse] = await Promise.all([
        ixcSourceService.list(),
        api.get('/companies'),
      ]);
      setSources(sourceResponse.data || []);
      setCompanies(companyResponse.data?.data || []);
    } catch (error) {
      notify(error.response?.data?.message || 'Não foi possível carregar as integrações IXC.', 'error');
    } finally {
      setLoading(false);
    }
  }, [canView, notify]);

  useEffect(() => { load(); }, [load]);

  const configuredCompanies = useMemo(() => new Set(sources.map((source) => Number(source.company_id))), [sources]);
  const availableCompanies = companies.filter((company) => !configuredCompanies.has(Number(company.id)));

  const handleAction = async (source, action) => {
    setBusyId(source.id);
    try {
      if (action === 'test') {
        const result = await ixcSourceService.test(source.id);
        notify(`${result.message}${result.latency_ms != null ? ` (${result.latency_ms} ms)` : ''}`);
      } else {
        const result = await ixcSourceService.toggle(source.id);
        notify(result.message);
      }
      await load();
    } catch (error) {
      notify(error.response?.data?.message || 'Não foi possível concluir a operação.', 'error');
      await load();
    } finally {
      setBusyId(null);
    }
  };

  if (!canView) return null;

  return (
    <div className="space-y-6">
      {notification && (
        <NotificationToast
          message={notification.message}
          type={notification.type}
          duration={5000}
          onClose={() => setNotification(null)}
        />
      )}

      <section className="relative overflow-hidden rounded-2xl border p-6 theme-surface" style={{ borderColor: 'var(--border)' }}>
        <div className="pointer-events-none absolute -right-16 -top-20 h-52 w-52 rounded-full bg-emerald-500/10 blur-3xl" />
        <div className="relative flex flex-col justify-between gap-5 md:flex-row md:items-end">
          <div className="max-w-2xl">
            <div className="mb-3 inline-flex items-center gap-2 rounded-full border border-emerald-500/25 bg-emerald-500/10 px-3 py-1 text-xs font-semibold uppercase tracking-[0.18em] text-emerald-500">
              <ShieldCheck className="h-3.5 w-3.5" /> Acesso controlado
            </div>
            <h2 className="text-2xl font-semibold theme-text">Integrações IXC</h2>
            <p className="mt-2 text-sm leading-6 theme-text-muted">Escolha banco direto ou MCP por empresa. Senhas e tokens permanecem cifrados.</p>
          </div>
          {canManage && (
            <button disabled={availableCompanies.length === 0} onClick={() => setModal({ open: true, source: null })} className="inline-flex items-center justify-center gap-2 rounded-xl bg-emerald-600 px-4 py-2.5 text-sm font-semibold text-white shadow-lg shadow-emerald-900/10 transition hover:-translate-y-0.5 hover:bg-emerald-500 disabled:cursor-not-allowed disabled:opacity-50">
              <Plus className="h-4 w-4" /> Nova integração
            </button>
          )}
        </div>
      </section>

      {loading ? (
        <div className="rounded-2xl border py-16 text-center theme-surface" style={{ borderColor: 'var(--border)' }}>
          <Loader2 className="mx-auto h-7 w-7 animate-spin text-emerald-500" />
          <p className="mt-3 text-sm theme-text-muted">Carregando fontes IXC...</p>
        </div>
      ) : sources.length === 0 ? (
        <div className="rounded-2xl border border-dashed py-16 text-center theme-surface" style={{ borderColor: 'var(--border)' }}>
          <Database className="mx-auto h-10 w-10 theme-text-muted" />
          <p className="mt-4 font-medium theme-text">Nenhuma integração configurada</p>
          <p className="mt-1 text-sm theme-text-muted">Cadastre a fonte IXC da primeira empresa para começar.</p>
        </div>
      ) : (
        <div className="grid gap-4 xl:grid-cols-2">
          {sources.map((source) => {
            const busy = busyId === source.id;
            const healthy = source.last_test_succeeded === true;
            return (
              <article key={source.id} className="group overflow-hidden rounded-2xl border theme-surface transition hover:-translate-y-0.5 hover:shadow-xl" style={{ borderColor: 'var(--border)' }}>
                <div className="flex items-start justify-between gap-4 border-b p-5" style={{ borderColor: 'var(--border)' }}>
                  <div className="flex min-w-0 items-center gap-3">
                    <div className={`rounded-xl p-2.5 ${source.is_active ? 'bg-emerald-500/10 text-emerald-500' : 'bg-slate-500/10 theme-text-muted'}`}>
                      <Building2 className="h-5 w-5" />
                    </div>
                    <div className="min-w-0">
                      <h3 className="truncate font-semibold theme-text">{source.company?.name || `Empresa ${source.company_id}`}</h3>
                      <p className="mt-0.5 truncate text-xs theme-text-muted">{source.adapter}</p>
                    </div>
                  </div>
                  <span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold ${source.is_active ? 'bg-emerald-500/10 text-emerald-500' : 'bg-slate-500/10 theme-text-muted'}`}>
                    {source.is_active ? <Activity className="h-3.5 w-3.5" /> : <CircleOff className="h-3.5 w-3.5" />}
                    {source.is_active ? 'Ativa' : 'Inativa'}
                  </span>
                </div>

                <div className="grid gap-3 p-5 sm:grid-cols-2">
                  <div className="rounded-xl border p-3" style={{ borderColor: 'var(--border)' }}>
                    <div className="flex items-center gap-2 text-xs uppercase tracking-wider theme-text-muted"><Server className="h-3.5 w-3.5" /> Endpoint</div>
                    <p className="mt-2 truncate font-mono text-sm theme-text">{source.connection_type === 'mcp' ? source.mcp_url : `${source.host}:${source.port}`} </p>
                  </div>
                  <div className="rounded-xl border p-3" style={{ borderColor: 'var(--border)' }}>
                    <div className="flex items-center gap-2 text-xs uppercase tracking-wider theme-text-muted"><Database className="h-3.5 w-3.5" /> Conexão</div>
                    <p className="mt-2 truncate font-mono text-sm theme-text">{source.connection_type === 'mcp' ? 'Via MCP' : `Banco direto · ${source.database}`} </p>
                  </div>
                </div>

                <div className="mx-5 mb-5 flex items-center justify-between gap-4 rounded-xl bg-black/[0.025] px-3 py-2.5 dark:bg-white/[0.035]">
                  <div className="min-w-0">
                    <p className={`text-xs font-semibold ${healthy ? 'text-emerald-500' : source.last_test_succeeded === false ? 'text-rose-500' : 'theme-text-muted'}`}>
                      {healthy ? 'Último teste aprovado' : source.last_test_succeeded === false ? 'Último teste falhou' : 'Conexão ainda não testada'}
                    </p>
                    <p className="mt-0.5 truncate text-xs theme-text-muted">{formatDate(source.last_tested_at)} · {source.connection_type === 'mcp' ? 'MCP' : `usuário ${source.username}`} </p>
                  </div>
                  {canManage && (
                    <div className="flex shrink-0 gap-1">
                      <button disabled={busy} onClick={() => handleAction(source, 'test')} className="rounded-lg p-2 theme-text-muted transition hover:bg-emerald-500/10 hover:text-emerald-500" title="Testar conexão"><RefreshCw className={`h-4 w-4 ${busy ? 'animate-spin' : ''}`} /></button>
                      <button disabled={busy} onClick={() => setModal({ open: true, source })} className="rounded-lg p-2 theme-text-muted transition hover:bg-sky-500/10 hover:text-sky-500" title="Editar"><Edit3 className="h-4 w-4" /></button>
                      <button disabled={busy} onClick={() => handleAction(source, 'toggle')} className="rounded-lg p-2 theme-text-muted transition hover:bg-amber-500/10 hover:text-amber-500" title={source.is_active ? 'Desativar' : 'Ativar'}><Power className="h-4 w-4" /></button>
                    </div>
                  )}
                </div>
              </article>
            );
          })}
        </div>
      )}

      <IxcSourceModal
        open={modal.open}
        source={modal.source}
        companies={modal.source ? companies : availableCompanies}
        onClose={() => setModal({ open: false, source: null })}
        onSaved={(message) => {
          setModal({ open: false, source: null });
          notify(message);
          load();
        }}
      />
    </div>
  );
};

export default IxcSources;
